import { supabase } from "@/lib/supabase";
import type { Tables } from "@synkro/db";

export type Attribute = Tables<"product_spec_fields">;

export interface AttributeRow extends Attribute {
  valuesCount: number;
  productsUsed: number;
}

export interface Quota {
  planCode: string;
  planName: string;
  used: number;
  maxAttributes: number | null;
  nextPlanCode: string | null;
  nextPlanName: string | null;
  nextPlanMax: number | null;
}

export async function fetchQuota(organizationId: string): Promise<Quota | null> {
  const { data, error } = await supabase.rpc("attribute_quota", {
    p_organization_id: organizationId,
  });
  if (error) throw error;

  const row = (data as unknown as Record<string, unknown>[])?.[0];
  if (!row) return null;

  return {
    planCode: row.plan_code as string,
    planName: row.plan_name as string,
    used: row.used as number,
    maxAttributes: (row.max_attributes as number | null) ?? null,
    nextPlanCode: (row.next_plan_code as string | null) ?? null,
    nextPlanName: (row.next_plan_name as string | null) ?? null,
    nextPlanMax: (row.next_plan_max as number | null) ?? null,
  };
}

export async function fetchAttributes(organizationId: string): Promise<AttributeRow[]> {
  const [fields, usage] = await Promise.all([
    supabase
      .from("product_spec_fields")
      .select("*")
      .eq("organization_id", organizationId)
      .order("position"),
    supabase.rpc("attribute_usage", { p_organization_id: organizationId }),
  ]);

  if (fields.error) throw fields.error;

  const uso = new Map(
    ((usage.data as unknown as Record<string, unknown>[]) ?? []).map((u) => [
      u.field_id as string,
      { valores: u.values_count as number, productos: u.products_used as number },
    ]),
  );

  return (fields.data ?? []).map((f) => ({
    ...f,
    valuesCount: uso.get(f.id)?.valores ?? 0,
    productsUsed: uso.get(f.id)?.productos ?? 0,
  }));
}

export interface AttributeDraft {
  label: string;
  dataType: string;
  unit: string | null;
  options: string[];
  appliesTo: "product" | "variant";
  filterable: boolean;
  active: boolean;
}

export function slugifyKey(label: string): string {
  return label
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
}

export async function createAttribute(organizationId: string, draft: AttributeDraft) {
  const key = slugifyKey(draft.label);
  if (!/^[a-z][a-z0-9_]{1,40}$/.test(key)) {
    throw new Error("El nombre del atributo no es válido.");
  }

  const { error } = await supabase.from("product_spec_fields").insert({
    organization_id: organizationId,
    key,
    label: draft.label.trim(),
    data_type: draft.dataType,
    unit: draft.dataType === "number" ? draft.unit || null : null,
    options: needsOptions(draft.dataType) ? draft.options : [],
    applies_to: draft.appliesTo,
    filterable: draft.filterable,
    active: draft.active,
  });

  if (error) throw error;
}

export async function updateAttribute(id: string, draft: AttributeDraft) {
  const { error } = await supabase
    .from("product_spec_fields")
    .update({
      label: draft.label.trim(),
      data_type: draft.dataType,
      unit: draft.dataType === "number" ? draft.unit || null : null,
      options: needsOptions(draft.dataType) ? draft.options : [],
      applies_to: draft.appliesTo,
      filterable: draft.filterable,
      active: draft.active,
    })
    .eq("id", id);

  if (error) throw error;
}

export async function deleteAttribute(id: string) {
  const { error } = await supabase.from("product_spec_fields").delete().eq("id", id);
  if (error) throw error;
}

/* --------------------------------------- catalogo recomendado por rubro */

export type AttributePreset = Tables<"attribute_presets">;

export async function fetchPresets(
  industry: string | null,
  organizationId: string,
): Promise<AttributePreset[]> {
  if (!industry) return [];

  const [presets, existing] = await Promise.all([
    supabase.from("attribute_presets").select("*").eq("industry", industry).order("position"),
    supabase.from("product_spec_fields").select("key").eq("organization_id", organizationId),
  ]);

  if (presets.error) throw presets.error;

  const ya = new Set((existing.data ?? []).map((f) => f.key));
  return (presets.data ?? []).filter((p) => !ya.has(p.key));
}

export async function adoptPresets(organizationId: string, keys: string[]) {
  const { data, error } = await supabase.rpc("adopt_attribute_presets", {
    p_organization_id: organizationId,
    p_keys: keys,
  });
  if (error) throw error;
  return data;
}

export function needsOptions(dataType: string): boolean {
  return dataType === "select" || dataType === "multiselect";
}

export const ATTRIBUTE_TYPES = [
  { id: "text", label: "Texto" },
  { id: "number", label: "Número" },
  { id: "select", label: "Selección única" },
  { id: "multiselect", label: "Selección múltiple" },
  { id: "color", label: "Color" },
  { id: "boolean", label: "Sí / No" },
  { id: "date", label: "Fecha" },
] as const;

export function typeLabel(dataType: string): string {
  return ATTRIBUTE_TYPES.find((t) => t.id === dataType)?.label ?? dataType;
}
